// ─── Phenotype interpretive content & display helpers ─────────────────────────
//
// Extracted from Phenotype.tsx for reuse by PrimaryDriverCard,
// SecondaryPatternCard, PHQContextCard, and ResearchBasis.
// ──────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { PhenotypeResult, PhenotypeProfile } from './phenotype';

// ─── Display helpers ─────────────────────────────────────────────────────────

export function levelBadge(level: PhenotypeResult['level'], _color: string) {
  const bg: Record<string, string> = {
    strong:   'bg-red-50 text-red-700 border-red-200',
    moderate: 'bg-amber-50 text-amber-700 border-amber-200',
    mild:     'bg-blue-50 text-blue-700 border-blue-200',
    minimal:  'bg-gray-50 text-gray-500 border-gray-200',
  };
  return bg[level];
}

export function screeningBadge(level: PhenotypeProfile['screeningLevel']) {
  switch (level) {
    case 'severe':   return { text: 'Significantly Elevated', bg: 'bg-red-50 text-red-700 border-red-200' };
    case 'moderate': return { text: 'Moderately Elevated', bg: 'bg-amber-50 text-amber-700 border-amber-200' };
    case 'mild':     return { text: 'Mildly Elevated', bg: 'bg-yellow-50 text-yellow-700 border-yellow-200' };
    default:         return { text: 'Within Typical Range', bg: 'bg-green-50 text-green-700 border-green-200' };
  }
}

export function phq4ScoreColor(score: number): string {
  if (score >= 9) return '#DC2626';
  if (score >= 6) return '#EA580C';
  if (score >= 3) return '#F59E0B';
  return '#16A34A';
}

export function formatChartDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ─── Signal Bar ──────────────────────────────────────────────────────────────

export function SignalBar({ score, color }: { score: number; color: string }) {
  return (
    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{ width: `${Math.min(100, score)}%`, backgroundColor: color }}
      />
    </div>
  );
}

// ─── Per-phenotype interpretive content ──────────────────────────────────────

export interface PhenotypeInterpretation {
  whatThisSuggests: string;
  whereToFocus: string;
  systems: { name: string; confidence: 'high confidence' | 'moderate confidence' | 'secondary' }[];
  careConversations: string;
  whatWouldChange: string[];
}

/** Fallback for any phenotype ID not yet in PHENOTYPE_INTERPRETATIONS. */
export const FALLBACK_INTERPRETATION: PhenotypeInterpretation = {
  whatThisSuggests:
    'A biological signal pattern has been detected based on your available data. More information will be available as the model evolves.',
  whereToFocus:
    'Discuss this pattern with your clinician for personalized guidance',
  systems: [],
  careConversations:
    'This pattern may warrant further exploration. Your clinician can help contextualize it within your broader health picture.',
  whatWouldChange: [
    'Additional check-ins and data will improve pattern clarity',
  ],
};

export const PHENOTYPE_INTERPRETATIONS: Record<string, PhenotypeInterpretation> = {
  hpa_dominant: {
    whatThisSuggests:
      'Your data shows a pattern most consistent with chronic stress-response activation and HPA-axis overactivation contributing to sleep, energy, and autonomic changes.',
    whereToFocus:
      'Stress physiology, autonomic recovery, and sleep-related arousal',
    systems: [
      { name: 'HPA axis / cortisol regulation', confidence: 'high confidence' },
      { name: 'Autonomic nervous system', confidence: 'moderate confidence' },
      { name: 'Sleep and circadian regulation', confidence: 'moderate confidence' },
      { name: 'Rumination / stress maintenance loops', confidence: 'secondary' },
    ],
    careConversations:
      'This pattern is often associated with sustained stress exposure and difficulty downregulating the physiological stress response. In similar profiles, discussions may include attention to stress physiology, sleep regularity, and autonomic recovery strategies.',
    whatWouldChange: [
      'Improved HRV and lower resting heart rate over 2\u20134 weeks',
      'Sleep score stabilization above 75 with consistent bedtime',
      'Reduction in self-reported stress and rumination scores',
    ],
  },
  immunometabolic: {
    whatThisSuggests:
      'Your data shows a pattern most consistent with systemic inflammation and metabolic dysregulation contributing to fatigue, appetite changes, and energy disruption.',
    whereToFocus:
      'Inflammatory markers, metabolic health, and nutritional status',
    systems: [
      { name: 'Neuroinflammatory signaling (CRP, IL-6)', confidence: 'high confidence' },
      { name: 'Insulin resistance / metabolic regulation', confidence: 'high confidence' },
      { name: 'Omega-3 / fatty acid balance', confidence: 'moderate confidence' },
      { name: 'Micronutrient status (Vitamin D, ferritin)', confidence: 'secondary' },
    ],
    careConversations:
      'This pattern is often associated with elevated inflammatory burden and metabolic imbalance. In similar profiles, discussions may include attention to inflammatory markers, metabolic health screening, and nutritional factors that influence immune signaling.',
    whatWouldChange: [
      'hsCRP trending below 3.0 mg/L over 8\u201312 weeks',
      'Improved fasting glucose and insulin sensitivity markers',
      'Sustained increase in physical activity and dietary quality scores',
    ],
  },
  anhedonic: {
    whatThisSuggests:
      'Your data shows a pattern most consistent with reduced reward-circuit activation and lower dopaminergic signaling contributing to diminished interest, motivation, and social engagement.',
    whereToFocus:
      'Reward system engagement, behavioral activation, and social connection',
    systems: [
      { name: 'Dopaminergic reward circuitry', confidence: 'high confidence' },
      { name: 'Behavioral activation / avoidance patterns', confidence: 'high confidence' },
      { name: 'Social engagement and connection', confidence: 'moderate confidence' },
      { name: 'Exercise and movement patterns', confidence: 'secondary' },
    ],
    careConversations:
      'This pattern is often associated with withdrawal from rewarding activities and reduced motivation. In similar profiles, discussions may include attention to behavioral activation strategies, graded re-engagement with pleasurable activities, and addressing avoidance maintenance loops.',
    whatWouldChange: [
      'Increase in daily activity and step counts over 2\u20134 weeks',
      'Improved social connection scores on check-in',
      'Reduction in avoidance behaviors (BADS) and increase in activation',
    ],
  },
  anxious: {
    whatThisSuggests:
      'Your data shows a pattern most consistent with autonomic dysregulation and elevated threat-response signaling contributing to arousal, somatic tension, and restlessness.',
    whereToFocus:
      'Autonomic regulation, threat processing, and worry/rumination patterns',
    systems: [
      { name: 'Autonomic nervous system (low HRV)', confidence: 'high confidence' },
      { name: 'Threat processing / amygdala reactivity', confidence: 'moderate confidence' },
      { name: 'Rumination and worry habit loops', confidence: 'moderate confidence' },
      { name: 'Cognitive interpretation biases', confidence: 'secondary' },
    ],
    careConversations:
      'This pattern is often associated with sustained sympathetic arousal and difficulty disengaging from worry cycles. In similar profiles, discussions may include attention to autonomic regulation, worry as a habit-based process, and approaches that target threat hypervigilance.',
    whatWouldChange: [
      'HRV improvement and resting heart rate reduction over 4\u20136 weeks',
      'Lower anxiety subscale scores (PHQ-4 Q3+Q4)',
      'Reduction in habitual negative thinking (HINT) and worry scores',
    ],
  },
};

// ─── Research references ─────────────────────────────────────────────────────

export interface ResearchRef {
  authors: string;
  title: string;
  journal: string;
  url: string;
}

export const RESEARCH_REFS: { category: string; refs: ResearchRef[] }[] = [
  {
    category: 'Phenotype Classification',
    refs: [
      { authors: 'Simmons et al., 2020', title: 'Appetite changes reveal depression subgroups with distinct endocrine, metabolic, and immune states', journal: 'Molecular Psychiatry', url: 'https://pubmed.ncbi.nlm.nih.gov/29899546/' },
      { authors: 'Simmons et al., 2016', title: 'Depression-related increases and decreases in appetite: dissociable patterns of aberrant activity in reward and interoceptive neurocircuitry', journal: 'American Journal of Psychiatry', url: 'https://pubmed.ncbi.nlm.nih.gov/26806872/' },
      { authors: 'Cerit et al., 2019', title: 'Divergent associations between ghrelin and neural responsivity to palatable food in hyperphagic and hypophagic depression', journal: 'J Affective Disorders', url: 'https://pubmed.ncbi.nlm.nih.gov/30170236/' },
      { authors: 'Pizzagalli, 2014', title: 'Depression, stress, and anhedonia: toward a synthesis and integrated model', journal: 'Annual Review of Clinical Psychology', url: 'https://pubmed.ncbi.nlm.nih.gov/24471371/' },
      { authors: 'Ionescu et al., 2015', title: 'Pharmacological approaches to the challenge of treatment-resistant depression', journal: 'Dialogues in Clinical Neuroscience', url: 'https://pubmed.ncbi.nlm.nih.gov/26246787/' },
    ],
  },
  {
    category: 'Mechanism & Intervention Research',
    refs: [
      { authors: 'Watkins et al., 2011', title: 'Rumination-focused cognitive-behavioural therapy for residual depression: phase II randomised controlled trial', journal: 'British Journal of Psychiatry', url: 'https://pubmed.ncbi.nlm.nih.gov/21778171/' },
      { authors: 'Brewer & Roy, 2021', title: 'Can approaching anxiety like a habit lead to novel treatments?', journal: 'American Journal of Lifestyle Medicine', url: 'https://pubmed.ncbi.nlm.nih.gov/34646095/' },
      { authors: 'Berg et al., 2023', title: 'A randomized clinical trial of behavioral activation and exposure-based therapy for adults with generalized anxiety disorder', journal: 'J Mood & Anxiety Disorders', url: 'https://pubmed.ncbi.nlm.nih.gov/38384390/' },
      { authors: 'Berg et al., 2025', title: 'Neural activity to reward and loss predicting treatment outcomes for adults with generalized anxiety disorder', journal: 'J Mood & Anxiety Disorders', url: 'https://pubmed.ncbi.nlm.nih.gov/40384942/' },
      { authors: 'Sublette et al., 2011', title: 'EPA-dominant omega-3 as antidepressant-active fraction', journal: 'J Clinical Psychiatry', url: 'https://pubmed.ncbi.nlm.nih.gov/21939614/' },
      { authors: 'Mathews & MacLeod, 2005', title: 'Cognitive vulnerability to emotional disorders', journal: 'Annual Review of Clinical Psychology', url: 'https://pubmed.ncbi.nlm.nih.gov/17716086/' },
    ],
  },
  {
    category: 'Assessment & Biomarkers',
    refs: [
      { authors: 'Mac Giollabhui et al., 2026', title: 'Effect of anti-inflammatory treatment on depressive symptom severity and anhedonia in depressed individuals with elevated inflammation', journal: 'American Journal of Psychiatry', url: 'https://pubmed.ncbi.nlm.nih.gov/41366844/' },
      { authors: 'Wang et al., 2025', title: 'Meta-analysis of the effect of vitamin D on depression', journal: 'Frontiers in Psychiatry', url: 'https://pubmed.ncbi.nlm.nih.gov/40821024/' },
      { authors: 'Kroenke et al., 2009', title: 'An ultra-brief screening scale for anxiety and depression: the PHQ-4', journal: 'Psychosomatics', url: 'https://pubmed.ncbi.nlm.nih.gov/19996233/' },
      { authors: 'Colvin et al., 2021', title: 'The automaticity of positive and negative thinking: a scoping review of mental habits', journal: 'Cognitive Therapy and Research', url: 'https://doi.org/10.1007/s10608-021-10218-4' },
      { authors: 'Strohmaier et al., 2021', title: 'Effects of length of mindfulness practice on mindfulness, depression, anxiety, and stress', journal: 'Mindfulness', url: 'https://doi.org/10.1007/s12671-020-01512-5' },
    ],
  },
];
